import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Calendar, MessageSquare, CheckCircle, XCircle, Clock, DollarSign, AlertCircle, IndianRupee } from 'lucide-react';
import axios from '@services/api/axios.instance';
import { toast } from 'react-hot-toast';

const PaymentDetails = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  useEffect(() => {
    fetchQuestionDetails();
  }, [questionId]);

  const fetchQuestionDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/admin/payments/${questionId}`);
      
      if (response.data.success) {
        const questionData = response.data.data.question;
        setQuestion(questionData);
        
        if (questionData.paymentStatus === 'unpaid') {
          setPaymentAmount(questionData.paymentAmount || '');
          setShowPaymentForm(true);
        }
      }
    } catch (error) {
      console.error('Error fetching question details:', error);
      toast.error('Failed to fetch question details');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(`/admin/payments/${questionId}/process`, {
        paymentAmount: parseFloat(paymentAmount),
        paymentRemarks
      });

      if (response.data.success) {
        toast.success('Payment processed successfully!');
        fetchQuestionDetails();
        setShowPaymentForm(false);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, label: 'Pending' },
      answered: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: CheckCircle, label: 'Answered' },
      approved: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    if (paymentStatus === 'paid') {
      return (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 border-2 border-green-300">
          <CheckCircle className="w-5 h-5" />
          Payment Completed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 border-2 border-red-300">
        <AlertCircle className="w-5 h-5" />
        Payment Pending
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Question Not Found</h2>
          <button
            onClick={() => navigate('/admin/payments')}
            className="text-green-600 hover:text-green-800"
          >
            Back to Payments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="w-full">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/payments')}
            className="flex items-center gap-2 Back to Payments text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Payments</span>
          </button>

          {getPaymentStatusBadge(question.paymentStatus)}
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="bg-[#1a2642] px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-white">Payment Details</h1>
              {getStatusBadge(question.status)}
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Student Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {question.student?.profilePicture ? (
                      <img
                        src={question.student.profilePicture}
                        alt={question.studentName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                        {question.studentName?.charAt(0) || 'S'}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{question.studentName}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {question.student?.email}
                      </p>
                      {question.student?.phone && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {question.student.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Mentor Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {question.mentor?.profilePicture ? (
                      <img
                        src={question.mentor.profilePicture}
                        alt={question.mentorName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold text-lg">
                        {question.mentorName?.charAt(0) || 'M'}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{question.mentorName}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {question.mentor?.email}
                      </p>
                      {question.mentor?.currentPosition && (
                        <p className="text-sm text-gray-600">{question.mentor.currentPosition}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Question</h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap">{question.question}</p>
                  <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Asked on {formatDate(question.questionDate)}
                    </span>
                  </div>
                </div>
              </div>

              {question.answer && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Answer</h3>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-gray-800 whitespace-pre-wrap">{question.answer}</p>
                    <div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Answered on {formatDate(question.answerDate)}
                      </span>
                      {question.rating && (
                        <span className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          Rating: {question.rating}/5
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {question.paymentStatus === 'paid' && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Payment Information</h3>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Payment Amount</p>
                        <p className="text-2xl font-bold text-green-700">{formatAmount(question.paymentAmount)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payment Date</p>
                        <p className="font-semibold text-gray-900">{formatDate(question.paymentDate)}</p>
                      </div>
                    </div>
                    {question.paymentRemarks && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600">Remarks</p>
                        <p className="text-gray-800">{question.paymentRemarks}</p>
                      </div>
                    )}
                    {question.paymentAdmin && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600">Processed By</p>
                        <p className="text-gray-800">
                          {question.paymentAdmin.firstName} {question.paymentAdmin.lastName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showPaymentForm && question.paymentStatus === 'unpaid' && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Process Payment</h3>
                  </div>
                  <form onSubmit={handleProcessPayment} className="bg-yellow-50 rounded-lg p-6 border-2 border-yellow-300">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Amount (₹) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <IndianRupee className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-semibold"
                          placeholder="Enter amount"
                          required
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">Enter the payment amount for this answered question</p>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Remarks (Optional)
                      </label>
                      <textarea
                        value={paymentRemarks}
                        onChange={(e) => setPaymentRemarks(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Add any remarks or notes about this payment..."
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={processing || !paymentAmount}
                        className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {processing ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            Process Payment
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPaymentForm(false)}
                        className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {!showPaymentForm && question.paymentStatus === 'unpaid' && (
                <div className="mb-6">
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="w-full bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <DollarSign className="w-5 h-5" />
                    Process Payment for this Question
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
